import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  ArrowRightIcon,
  BookmarkIcon,
  GlobeAltIcon,
  SparklesIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartSolid,
  BookmarkIcon as BookmarkSolid,
} from '@heroicons/react/24/solid';
import { HeartIcon } from '@heroicons/react/24/outline';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import ExploreGoogleMap from '../components/ExploreGoogleMap';
import SwipeOnboarding from '../components/onboarding/SwipeOnboarding';
import mockItineraries from '../data/mockItineraries';
import { exploreApi } from '../services/api';
import { getUserId } from '../utils/userId';
import { getSurpriseParams } from '../utils/surpriseMe';
import useWikipediaImage from '../hooks/useWikipediaImage';
import { STYLE_GRADIENTS, STYLES, STYLE_LABELS, REGIONS, COST_PRESETS, DAYS_PRESETS, SORT_OPTIONS } from '../data/filterOptions';
import {
  PageWrapper,
  ExplorerLayout, Sidebar, SidebarHeader, PageTitle,
  ViewToggleWrapper, ViewToggleButton,
  FilterRow, FilterChip, AISortChip,
  CardList, CardListInner,
  ItinCard, DesktopCardInner, CardThumbnail, CardBody,
  CardTitleRow, CardTitle, FavoriteIcon, CardMeta, CardHighlights, CardUser,
  MobileCardInner, MobileCardImage, MobileCardFavorite, MobileCardContent,
  MobileCardTitle, MobileCardMeta, MobileCardHighlights, MobileCardFooter,
  SaveButton, StyleBadge, ExploreLink,
  MapPanel, MapListViewButton,
  EmptyState, EmptyIcon, EmptyTitle, EmptyText, ResetFiltersButton,
  MapPeekCard, PeekCardImage, PeekCardContent, PeekCardTitle,
  PeekCardMeta, PeekCardFooter, PeekCardRating, PeekCardLink,
  GradientPlaceholder, DefaultAvatar, NewBadge,
  FilterDropdown, FilterDropdownItem,
  SkeletonCard, SkeletonBlock,
  SortFallbackBadge, ScrollSentinel, SurpriseButton,
} from './ItineraryExplorerPage.styles';

const PAGE_SIZE = 20;

// Approximate capital-city coordinates per country — used as fallback when the
// backend hasn't yet populated lat/lng on the itinerary row.
const COUNTRY_COORDS = {
  france: [48.8566, 2.3522], italy: [41.9028, 12.4964], spain: [40.4168, -3.7038],
  germany: [52.52, 13.405], 'united kingdom': [51.5074, -0.1278], uk: [51.5074, -0.1278],
  greece: [37.9838, 23.7275], portugal: [38.7223, -9.1393], japan: [35.6762, 139.6503],
  china: [39.9042, 116.4074], india: [28.6139, 77.209], thailand: [13.7563, 100.5018],
  vietnam: [21.0278, 105.8342], indonesia: [-6.2088, 106.8456], singapore: [1.3521, 103.8198],
  'south korea': [37.5665, 126.978], usa: [38.9072, -77.0369], 'united states': [38.9072, -77.0369],
  canada: [45.4215, -75.6972], mexico: [19.4326, -99.1332], brazil: [-15.7975, -47.8919],
  argentina: [-34.6037, -58.3816], peru: [-12.0464, -77.0428], colombia: [4.711, -74.0721],
  chile: [-33.4489, -70.6693], egypt: [30.0444, 31.2357], morocco: [33.9716, -6.8498],
  'south africa': [-33.9249, 18.4241], kenya: [-1.2921, 36.8219], tanzania: [-6.7924, 39.2083],
  australia: [-35.2809, 149.13], 'new zealand': [-41.2865, 174.7762],
  uae: [24.4539, 54.3773], 'united arab emirates': [24.4539, 54.3773],
  jordan: [31.9454, 35.9284], turkey: [39.9334, 32.8597], israel: [31.7683, 35.2137],
  netherlands: [52.3676, 4.9041], switzerland: [46.948, 7.4474], austria: [48.2082, 16.3738],
  'czech republic': [50.0755, 14.4378], poland: [52.2297, 21.0122], iceland: [64.1466, -21.9426],
  croatia: [45.815, 15.9819], hungary: [47.4979, 19.0402], cambodia: [11.5564, 104.9282],
  nepal: [27.7172, 85.324], 'sri lanka': [6.9271, 79.8612], 'costa rica': [9.9281, -84.0907],
  philippines: [14.5995, 120.9842], taiwan: [25.033, 121.5654], maldives: [4.1755, 73.5093],
};

// Curated Unsplash photos per country — shown when the backend hasn't set coverImageUrl
// City-specific images (keyed by cityId slug from backend)
const CITY_IMAGES = {
  // India
  lucknow:          'https://images.unsplash.com/photo-1598091383021-15ddea10925d?w=400&q=75',
  agra:             'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=400&q=75',
  delhi:            'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400&q=75',
  'new-delhi':      'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400&q=75',
  mumbai:           'https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=400&q=75',
  jaipur:           'https://images.unsplash.com/photo-1477587458883-47145ed31d4b?w=400&q=75',
  varanasi:         'https://images.unsplash.com/photo-1561361058-c24cecae35ca?w=400&q=75',
  goa:              'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=75',
  kerala:           'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=75',
  bangalore:        'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=400&q=75',
  bengaluru:        'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=400&q=75',
  hyderabad:        'https://images.unsplash.com/photo-1572445271230-a78d5b1f738a?w=400&q=75',
  chennai:          'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400&q=75',
  kolkata:          'https://images.unsplash.com/photo-1558431382-27e303142255?w=400&q=75',
  udaipur:          'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=400&q=75',
  jodhpur:          'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400&q=75',
  rishikesh:        'https://images.unsplash.com/photo-1591018533073-cf3b5be98e7f?w=400&q=75',
  amritsar:         'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400&q=75',
  mysore:           'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=400&q=75',
  mysuru:           'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=400&q=75',
  pune:             'https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=400&q=75',
  ahmedabad:        'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&q=75',
  // Asia
  tokyo:            'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=75',
  kyoto:            'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=400&q=75',
  osaka:            'https://images.unsplash.com/photo-1590559899731-a382839e5549?w=400&q=75',
  bangkok:          'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=400&q=75',
  'chiang-mai':     'https://images.unsplash.com/photo-1527838832700-5059252407fa?w=400&q=75',
  bali:             'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=75',
  singapore:        'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=400&q=75',
  'ho-chi-minh':    'https://images.unsplash.com/photo-1583417319070-4a69db38a482?w=400&q=75',
  hanoi:            'https://images.unsplash.com/photo-1557750255-c76072a7aad1?w=400&q=75',
  beijing:          'https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=400&q=75',
  shanghai:         'https://images.unsplash.com/photo-1474181487882-5abf3f0ba6c2?w=400&q=75',
  seoul:            'https://images.unsplash.com/photo-1601621915196-2621bfb0cd6e?w=400&q=75',
  kathmandu:        'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400&q=75',
  dubai:            'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=75',
  // Europe
  paris:            'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=400&q=75',
  london:           'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=75',
  rome:             'https://images.unsplash.com/photo-1523531294919-4bcd7c65e216?w=400&q=75',
  barcelona:        'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&q=75',
  amsterdam:        'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&q=75',
  prague:           'https://images.unsplash.com/photo-1509357599666-cb21e3a4f0e5?w=400&q=75',
  vienna:           'https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=400&q=75',
  budapest:         'https://images.unsplash.com/photo-1590253230532-a67f6bc61c9e?w=400&q=75',
  athens:           'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=400&q=75',
  lisbon:           'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&q=75',
  istanbul:         'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=400&q=75',
  reykjavik:        'https://images.unsplash.com/photo-1504829857797-ddff29c27927?w=400&q=75',
  // Americas
  'new-york':       'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=400&q=75',
  'los-angeles':    'https://images.unsplash.com/photo-1503891450247-ee5f8ec46dc3?w=400&q=75',
  'san-francisco':  'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&q=75',
  'mexico-city':    'https://images.unsplash.com/photo-1512813195386-6cf811ad3542?w=400&q=75',
  'rio-de-janeiro': 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=400&q=75',
  'buenos-aires':   'https://images.unsplash.com/photo-1589909202802-8f4aadce9a3c?w=400&q=75',
  lima:             'https://images.unsplash.com/photo-1526392060635-9d6019884377?w=400&q=75',
  // Africa & Middle East
  cairo:            'https://images.unsplash.com/photo-1539768942893-daf53e448371?w=400&q=75',
  marrakech:        'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=400&q=75',
  'cape-town':      'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=400&q=75',
  petra:            'https://images.unsplash.com/photo-1579606032821-4e6161c81bd3?w=400&q=75',
  // Oceania
  sydney:           'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=400&q=75',
  'queenstown':     'https://images.unsplash.com/photo-1493816628575-b2c912bc89df?w=400&q=75',
};

// Country-level fallback images
const COUNTRY_IMAGES = {
  japan:            'https://images.unsplash.com/photo-1480796927426-f609979314bd?w=400&q=75',
  france:           'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=400&q=75',
  italy:            'https://images.unsplash.com/photo-1523531294919-4bcd7c65e216?w=400&q=75',
  spain:            'https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=400&q=75',
  germany:          'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=400&q=75',
  'united kingdom': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=75',
  uk:               'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=75',
  greece:           'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=400&q=75',
  portugal:         'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&q=75',
  thailand:         'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=400&q=75',
  vietnam:          'https://images.unsplash.com/photo-1506461883276-594a12b5b1de?w=400&q=75',
  indonesia:        'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=75',
  singapore:        'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=400&q=75',
  'south korea':    'https://images.unsplash.com/photo-1601621915196-2621bfb0cd6e?w=400&q=75',
  india:            'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&q=75',
  china:            'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?w=400&q=75',
  usa:              'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=400&q=75',
  'united states':  'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=400&q=75',
  canada:           'https://images.unsplash.com/photo-1474900879168-3e0c35e6d8c3?w=400&q=75',
  mexico:           'https://images.unsplash.com/photo-1512813195386-6cf811ad3542?w=400&q=75',
  brazil:           'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=400&q=75',
  argentina:        'https://images.unsplash.com/photo-1589909202802-8f4aadce9a3c?w=400&q=75',
  peru:             'https://images.unsplash.com/photo-1526392060635-9d6019884377?w=400&q=75',
  colombia:         'https://images.unsplash.com/photo-1566232392379-afd9298e6a46?w=400&q=75',
  chile:            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=400&q=75',
  egypt:            'https://images.unsplash.com/photo-1539768942893-daf53e448371?w=400&q=75',
  morocco:          'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=400&q=75',
  'south africa':   'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=400&q=75',
  kenya:            'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=400&q=75',
  tanzania:         'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=400&q=75',
  australia:        'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=400&q=75',
  'new zealand':    'https://images.unsplash.com/photo-1493816628575-b2c912bc89df?w=400&q=75',
  uae:              'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=75',
  'united arab emirates': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=75',
  jordan:           'https://images.unsplash.com/photo-1579606032821-4e6161c81bd3?w=400&q=75',
  turkey:           'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=400&q=75',
  israel:           'https://images.unsplash.com/photo-1544948503-7ad532b80918?w=400&q=75',
  netherlands:      'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&q=75',
  switzerland:      'https://images.unsplash.com/photo-1531198762800-a4cce7e3d5cb?w=400&q=75',
  austria:          'https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=400&q=75',
  'czech republic': 'https://images.unsplash.com/photo-1509357599666-cb21e3a4f0e5?w=400&q=75',
  poland:           'https://images.unsplash.com/photo-1519197924294-4ba991a11128?w=400&q=75',
  iceland:          'https://images.unsplash.com/photo-1504829857797-ddff29c27927?w=400&q=75',
  croatia:          'https://images.unsplash.com/photo-1555990793-da11153b2473?w=400&q=75',
  hungary:          'https://images.unsplash.com/photo-1590253230532-a67f6bc61c9e?w=400&q=75',
  cambodia:         'https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=400&q=75',
  nepal:            'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400&q=75',
  'sri lanka':      'https://images.unsplash.com/photo-1578922746465-3a80a228f223?w=400&q=75',
  'costa rica':     'https://images.unsplash.com/photo-1518259102261-b40117eabbc9?w=400&q=75',
  philippines:      'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=400&q=75',
  taiwan:           'https://images.unsplash.com/photo-1598518142724-804e8ccf5fca?w=400&q=75',
  maldives:         'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=400&q=75',
};

// city (cityId slug from backend) is checked first; country is the fallback
function getDestinationImage(city, country) {
  if (city) {
    const cityImg = CITY_IMAGES[city.toLowerCase()];
    if (cityImg) return cityImg;
  }
  if (country) {
    return COUNTRY_IMAGES[country.toLowerCase()] || null;
  }
  return null;
}

function getCoordsFallback(country) {
  if (!country) return null;
  const coords = COUNTRY_COORDS[country.toLowerCase().trim()];
  return coords ? { lat: coords[0], lng: coords[1] } : null;
}

// Validate a mapPosition — reject null, NaN, and (0,0) which is a common bad-data sentinel.
function isValidMapPosition(pos) {
  if (!pos) return false;
  const lat = Number(pos.lat);
  const lng = Number(pos.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  return true;
}

// ===== URL SEARCH PARAM HELPERS =====
function readFiltersFromParams(searchParams) {
  return {
    style: searchParams.get('style') || null,
    region: searchParams.get('region') || null,
    costMax: searchParams.get('cost_max') ? Number(searchParams.get('cost_max')) : null,
    costMin: searchParams.get('cost_min') ? Number(searchParams.get('cost_min')) : null,
    daysMin: searchParams.get('days_min') ? Number(searchParams.get('days_min')) : null,
    daysMax: searchParams.get('days_max') ? Number(searchParams.get('days_max')) : null,
    sort: searchParams.get('sort') || 'newest',
    q: searchParams.get('q') || '',
  };
}

// Build API params from filters
function filtersToApiParams(filters) {
  const params = { size: PAGE_SIZE };
  const userId = getUserId();
  if (userId) params.userId = userId;
  if (filters.style) params.style = filters.style;
  if (filters.region) params.region = filters.region;
  if (filters.costMin != null) params.cost_min = filters.costMin;
  if (filters.costMax != null) params.cost_max = filters.costMax;
  if (filters.daysMin != null) params.days_min = filters.daysMin;
  if (filters.daysMax != null) params.days_max = filters.daysMax;
  if (filters.sort && filters.sort !== 'newest') params.sort = filters.sort;
  if (filters.q) params.q = filters.q;
  return params;
}

// ===== SKELETON CARD COMPONENT =====
function SkeletonCardComponent() {
  return (
    <SkeletonCard>
      <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem' }}>
        <SkeletonBlock $width="96px" $height="72px" />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <SkeletonBlock $width="75%" $height="14px" />
          <SkeletonBlock $width="50%" $height="11px" />
          <SkeletonBlock $width="90%" $height="11px" />
          <SkeletonBlock $width="30%" $height="10px" />
        </div>
      </div>
    </SkeletonCard>
  );
}

function ItineraryExplorerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // First-visit swipe onboarding
  const [showVibeBanner, setShowVibeBanner] = useState(() => {
    try { return !localStorage.getItem('travelDNA') && !localStorage.getItem('vibeBannerDismissed'); } catch { return false; }
  });
  const [showSwipeOnboarding, setShowSwipeOnboarding] = useState(false);

  // Derive filters from URL search params
  const filters = readFiltersFromParams(searchParams);

  // Local draft for search input — debounced to URL to avoid query-on-every-keystroke
  const [searchDraft, setSearchDraft] = useState(() => searchParams.get('q') || '');

  // Debounce: push searchDraft to URL after 300ms idle
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchDraft) {
        next.set('q', searchDraft);
      } else {
        next.delete('q');
      }
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value != null && value !== '') {
      next.set(key, String(value));
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    setSearchDraft('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // ===== INFINITE QUERY: explore feed =====
  const apiParams = useMemo(() => filtersToApiParams(filters), [filters]);
  const apiParamsRef = useRef(apiParams);
  apiParamsRef.current = apiParams;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useInfiniteQuery(
    ['explore', apiParams],
    async ({ pageParam = 0 }) => {
      const res = await exploreApi.getExplore({ ...apiParams, page: pageParam });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.page < lastPage.totalPages - 1) {
          return lastPage.page + 1;
        }
        return undefined;
      },
      onError: (err) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Explore API failed, falling back to mock data:', err?.message);
        }
      },
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );

  // Flatten pages into single itineraries array, or fall back to mock in dev
  const itineraries = useMemo(() => {
    if (data?.pages?.length) {
      return data.pages.flatMap(p => p.itineraries || []);
    }
    // Dev fallback: use mock data when API is unavailable
    if (isError && process.env.NODE_ENV === 'development') {
      return mockItineraries;
    }
    return [];
  }, [data, isError]);

  // Extract metadata from first page response
  const firstPage = data?.pages?.[0];
  const sortApplied = firstPage?.sortApplied || null;

  // Cap map pins to 20; enrich with country-derived coords when mapPosition is absent or invalid.
  const mapPins = useMemo(() =>
    itineraries
      .map(it => {
        const apiPos = isValidMapPosition(it.mapPosition) ? it.mapPosition : null;
        return { ...it, mapPosition: apiPos || getCoordsFallback(it.country) };
      })
      .filter(it => it.mapPosition)
      .slice(0, 20),
  [itineraries]);

  // Local UI state
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [activeView, setActiveView] = useState('list');
  const [openDropdown, setOpenDropdown] = useState(null);

  // Surprise Me cooldown (60s)
  const [surpriseCooldown, setSurpriseCooldown] = useState(() => {
    try {
      const until = parseInt(sessionStorage.getItem('surpriseMeCooldownUntil') || '0', 10);
      const remaining = Math.ceil((until - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    } catch { return 0; }
  });

  useEffect(() => {
    if (surpriseCooldown <= 0) return;
    const timer = setInterval(() => {
      setSurpriseCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [surpriseCooldown]);

  // Used to scroll a card into view when a map pin is clicked
  const cardRefs = useRef({});

  // Select the first itinerary once loaded
  useEffect(() => {
    if (itineraries.length > 0 && selectedId == null) {
      setSelectedId(itineraries[0].id);
    }
  }, [itineraries, selectedId]);

  // Close dropdown on outside click
  const dropdownRef = useRef(null);
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    if (openDropdown) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [openDropdown]);

  // ===== INFINITE SCROLL: IntersectionObserver sentinel =====
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ===== OPTIMISTIC SAVE/UNSAVE MUTATION =====
  const saveMutation = useMutation(
    (id) => exploreApi.toggleSave(id),
    {
      onMutate: async (id) => {
        const currentParams = apiParamsRef.current;
        // Cancel outgoing refetches
        await queryClient.cancelQueries(['explore']);
        // Snapshot previous data
        const previousData = queryClient.getQueryData(['explore', currentParams]);
        // Optimistically flip isFavorited and adjust saveCount
        queryClient.setQueryData(['explore', currentParams], (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              itineraries: page.itineraries.map(it => {
                if (it.id !== id) return it;
                const wasFav = it.isFavorited;
                return {
                  ...it,
                  isFavorited: !wasFav,
                  saveCount: wasFav ? Math.max(0, it.saveCount - 1) : it.saveCount + 1,
                };
              }),
            })),
          };
        });
        return { previousData, currentParams };
      },
      onError: (err, id, context) => {
        // Revert optimistic update
        if (context?.previousData) {
          queryClient.setQueryData(['explore', context.currentParams], context.previousData);
        }
        if (err?.response?.status === 401) {
          toast.info('Sign in to save itineraries', { toastId: 'save-auth' });
        } else {
          toast.error('Failed to save. Please try again.');
        }
      },
      onSuccess: (res, id, context) => {
        // Update saveCount from server response if available
        const serverData = res?.data;
        if (serverData?.saveCount != null) {
          queryClient.setQueryData(['explore', context.currentParams], (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                itineraries: page.itineraries.map(it =>
                  it.id === id ? { ...it, saveCount: serverData.saveCount } : it
                ),
              })),
            };
          });
        }
      },
    }
  );

  const toggleFavorite = useCallback((id) => {
    saveMutation.mutate(id);
  }, [saveMutation]);

  const selectedItinerary = useMemo(() =>
    itineraries.find(it => it.id === selectedId) || itineraries[0] || null,
    [itineraries, selectedId]
  );

  // Handlers
  const handleCardSelect = useCallback((id) => {
    setSelectedId(id);
    const itinerary = itineraries.find(i => i.id === id);
    if (itinerary?.slug) {
      navigate(`/itinerary/${itinerary.slug}`);
    } else if (id) {
      navigate(`/itineraries/${id}`);
    }
  }, [itineraries, navigate]);

  const handlePinClick = useCallback((id) => {
    setSelectedId(id);
    // Scroll the matching card into view in the sidebar
    setTimeout(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }, []);

  const handleCostPreset = useCallback((preset) => {
    const next = new URLSearchParams(searchParams);
    const isActive = (filters.costMin === (preset.min || null)) && (filters.costMax === (preset.max || null));
    if (isActive) {
      next.delete('cost_min');
      next.delete('cost_max');
    } else {
      if (preset.min != null) next.set('cost_min', preset.min); else next.delete('cost_min');
      if (preset.max != null) next.set('cost_max', preset.max); else next.delete('cost_max');
    }
    setSearchParams(next, { replace: true });
    setOpenDropdown(null);
  }, [searchParams, setSearchParams, filters.costMin, filters.costMax]);

  const handleDaysPreset = useCallback((preset) => {
    const next = new URLSearchParams(searchParams);
    const isActive = (filters.daysMin === (preset.min || null)) && (filters.daysMax === (preset.max || null));
    if (isActive) {
      next.delete('days_min');
      next.delete('days_max');
    } else {
      if (preset.min != null) next.set('days_min', preset.min); else next.delete('days_min');
      if (preset.max != null) next.set('days_max', preset.max); else next.delete('days_max');
    }
    setSearchParams(next, { replace: true });
    setOpenDropdown(null);
  }, [searchParams, setSearchParams, filters.daysMin, filters.daysMax]);

  // Helper: get current sort label
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === filters.sort)?.label || 'Newest';

  // Sort fallback indicator
  const showSortFallback = sortApplied === 'popular_fallback' && filters.sort === 'ai_match';

  // ===== RENDER =====
  return (
    <>
    <AnimatePresence>
      {showSwipeOnboarding && (
        <SwipeOnboarding
          onComplete={() => { setShowSwipeOnboarding(false); setShowVibeBanner(false); }}
          onClose={() => setShowSwipeOnboarding(false)}
        />
      )}
    </AnimatePresence>
    <PageWrapper $mapActive={activeView === 'map'}>
      {/* Main Layout */}
      <ExplorerLayout>
        {/* Sidebar — removed from DOM in mobile map view so it holds zero space.
             On desktop activeView never reaches 'map' (no UI trigger), so this is safe. */}
        {activeView !== 'map' && <Sidebar>
          <SidebarHeader>
            {showVibeBanner && (
              <VibeBanner>
                <VibeBannerText>
                  <SparklesIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
                  Discover your travel vibe for personalized results
                </VibeBannerText>
                <VibeBannerActions>
                  <VibeBannerCTA onClick={() => setShowSwipeOnboarding(true)}>
                    Take the Quiz
                  </VibeBannerCTA>
                  <VibeBannerDismiss onClick={() => {
                    setShowVibeBanner(false);
                    try { localStorage.setItem('vibeBannerDismissed', '1'); } catch {}
                  }}>
                    {'\u2715'}
                  </VibeBannerDismiss>
                </VibeBannerActions>
              </VibeBanner>
            )}
            <PageTitle>Explore Itineraries</PageTitle>

            <SurpriseButton
              disabled={surpriseCooldown > 0}
              onClick={() => {
                if (surpriseCooldown > 0) return;
                const params = getSurpriseParams();
                const cooldownUntil = Date.now() + 60000;
                try { sessionStorage.setItem('surpriseMeCooldownUntil', String(cooldownUntil)); } catch {}
                setSurpriseCooldown(60);
                navigate('/itineraries', {
                  state: {
                    formData: {
                      place: params.country,
                      days: params.days,
                      itineraryType: params.itineraryType,
                      budget: params.budgetRange,
                      travelPreferences: params.travelPreferences,
                      travelerProfile: params.travelPersonality,
                      transportation: 'public',
                    },
                    autoGenerate: true,
                    isSurprise: true,
                  },
                });
              }}>
              <SparklesIcon />
              {surpriseCooldown > 0 ? `Surprise Me (${surpriseCooldown}s)` : 'Surprise Me'}
            </SurpriseButton>

            {/* Mobile View Toggle */}
            <ViewToggleWrapper>
              <ViewToggleButton $active={activeView === 'list'} onClick={() => setActiveView('list')}>
                List View
              </ViewToggleButton>
              <ViewToggleButton $active={activeView === 'map'} onClick={() => setActiveView('map')}>
                Map View
              </ViewToggleButton>
            </ViewToggleWrapper>

            {/* Filter Chips */}
            <FilterRow ref={dropdownRef}>
              {/* Cost filter */}
              <div style={{ position: 'relative' }}>
                <FilterChip
                  $active={filters.costMin != null || filters.costMax != null}
                  onClick={() => setOpenDropdown(prev => prev === 'cost' ? null : 'cost')}
                >
                  Cost <ChevronDownIcon />
                </FilterChip>
                {openDropdown === 'cost' && (
                  <FilterDropdown
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                  >
                    {COST_PRESETS.map(preset => {
                      const isActive = (filters.costMin === (preset.min || null)) && (filters.costMax === (preset.max || null));
                      return (
                        <FilterDropdownItem
                          key={preset.label}
                          $active={isActive}
                          onClick={() => handleCostPreset(preset)}
                        >
                          {preset.label}
                        </FilterDropdownItem>
                      );
                    })}
                  </FilterDropdown>
                )}
              </div>

              {/* Days filter */}
              <div style={{ position: 'relative' }}>
                <FilterChip
                  $active={filters.daysMin != null || filters.daysMax != null}
                  onClick={() => setOpenDropdown(prev => prev === 'days' ? null : 'days')}
                >
                  Days <ChevronDownIcon />
                </FilterChip>
                {openDropdown === 'days' && (
                  <FilterDropdown
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {DAYS_PRESETS.map(preset => {
                      const isActive = (filters.daysMin === (preset.min || null)) && (filters.daysMax === (preset.max || null));
                      return (
                        <FilterDropdownItem
                          key={preset.label}
                          $active={isActive}
                          onClick={() => handleDaysPreset(preset)}
                        >
                          {preset.label}
                        </FilterDropdownItem>
                      );
                    })}
                  </FilterDropdown>
                )}
              </div>

              {/* Style filter */}
              <div style={{ position: 'relative' }}>
                <FilterChip
                  $active={!!filters.style}
                  onClick={() => setOpenDropdown(prev => prev === 'style' ? null : 'style')}
                >
                  {filters.style ? STYLE_LABELS[filters.style] : 'Style'} <ChevronDownIcon />
                </FilterChip>
                {openDropdown === 'style' && (
                  <FilterDropdown
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {STYLES.map(style => (
                      <FilterDropdownItem
                        key={style}
                        $active={filters.style === style}
                        onClick={() => {
                          updateFilter('style', filters.style === style ? null : style);
                          setOpenDropdown(null);
                        }}
                      >
                        {STYLE_LABELS[style]}
                      </FilterDropdownItem>
                    ))}
                  </FilterDropdown>
                )}
              </div>

              {/* Region filter */}
              <div style={{ position: 'relative' }}>
                <FilterChip
                  $active={!!filters.region}
                  onClick={() => setOpenDropdown(prev => prev === 'region' ? null : 'region')}
                >
                  {filters.region || 'Region'} <ChevronDownIcon />
                </FilterChip>
                {openDropdown === 'region' && (
                  <FilterDropdown
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {REGIONS.map(region => (
                      <FilterDropdownItem
                        key={region}
                        $active={filters.region === region}
                        onClick={() => {
                          updateFilter('region', filters.region === region ? null : region);
                          setOpenDropdown(null);
                        }}
                      >
                        {region}
                      </FilterDropdownItem>
                    ))}
                  </FilterDropdown>
                )}
              </div>

              {/* Sort chip */}
              <div style={{ position: 'relative', marginLeft: 'auto' }}>
                <AISortChip onClick={() => setOpenDropdown(prev => prev === 'sort' ? null : 'sort')}>
                  <SparklesIcon style={{ width: 12, height: 12 }} />
                  {currentSortLabel}
                  {showSortFallback && (
                    <SortFallbackBadge>
                      <InformationCircleIcon /> Popular fallback
                    </SortFallbackBadge>
                  )}
                  <ChevronDownIcon />
                </AISortChip>
                {openDropdown === 'sort' && (
                  <FilterDropdown
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ right: 0, left: 'auto' }}
                  >
                    {SORT_OPTIONS.map(opt => (
                      <FilterDropdownItem
                        key={opt.value}
                        $active={filters.sort === opt.value}
                        onClick={() => {
                          updateFilter('sort', opt.value === 'newest' ? null : opt.value);
                          setOpenDropdown(null);
                        }}
                      >
                        {opt.label}
                      </FilterDropdownItem>
                    ))}
                  </FilterDropdown>
                )}
              </div>
            </FilterRow>
          </SidebarHeader>

          {/* Card List */}
          <CardList>
            <CardListInner>
              {isLoading ? (
                <>
                  <SkeletonCardComponent />
                  <SkeletonCardComponent />
                  <SkeletonCardComponent />
                </>
              ) : itineraries.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><FunnelIcon /></EmptyIcon>
                  {(() => {
                    try {
                      const dna = JSON.parse(localStorage.getItem('travelDNA') || 'null');
                      if (dna?.dimensions) {
                        const activeStyle = searchParams.get('style') || '';
                        return (
                          <>
                            <EmptyTitle>No {activeStyle || 'matching'} trips yet</EmptyTitle>
                            <EmptyText>Want us to create one tailored to your Travel DNA?</EmptyText>
                            <ResetFiltersButton
                              onClick={() => navigate(`/itineraries?prefill=true${activeStyle ? `&style=${activeStyle}` : ''}`)}
                              style={{ background: 'linear-gradient(135deg, #4285F4 0%, #1A73E8 100%)', color: 'white', border: 'none' }}
                            >
                              Create for me
                            </ResetFiltersButton>
                          </>
                        );
                      }
                    } catch { /* ignore */ }
                    return (
                      <>
                        <EmptyTitle>No itineraries match your filters</EmptyTitle>
                        <EmptyText>Discover your travel style first to get personalized suggestions.</EmptyText>
                      </>
                    );
                  })()}
                  <ResetFiltersButton onClick={clearAllFilters}>Reset Filters</ResetFiltersButton>
                </EmptyState>
              ) : (
                <AnimatePresence key={JSON.stringify(apiParams)} initial>
                  {itineraries.map((itinerary, index) => (
                    <motion.div
                      key={itinerary.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.5) }}
                    >
                      <ItineraryCardComponent
                        itinerary={itinerary}
                        isSelected={selectedId === itinerary.id}
                        isFavorited={itinerary.isFavorited}
                        onSelect={handleCardSelect}
                        onToggleFavorite={toggleFavorite}
                        onHover={() => setHoveredCardId(itinerary.id)}
                        onHoverEnd={() => setHoveredCardId(null)}
                        registerRef={(el) => { cardRefs.current[itinerary.id] = el; }}
                      />
                    </motion.div>
                  ))}
                  {isFetchingNextPage && (
                    <>
                      <SkeletonCardComponent />
                      <SkeletonCardComponent />
                    </>
                  )}
                  <ScrollSentinel ref={sentinelRef} />
                </AnimatePresence>
              )}
            </CardListInner>
          </CardList>
        </Sidebar>}

        {/* Map Panel — inside ExplorerLayout for desktop (flex sibling of sidebar) */}
        <MapPanel $mobileVisible={activeView === 'map'}>
          {/* Floating button to return to list view on mobile */}
          {activeView === 'map' && (
            <MapListViewButton onClick={() => setActiveView('list')}>
              <ListBulletIcon /> List View
            </MapListViewButton>
          )}
          <ExploreGoogleMap
            itineraries={mapPins}
            selectedId={selectedId}
            hoveredId={hoveredCardId}
            onSelectItinerary={handlePinClick}
            visible={activeView === 'map'}
          />

          {/* Mobile Map Peek Card */}
          {activeView === 'map' && selectedItinerary && (
            <AnimatePresence>
              <MapPeekCard
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                key={selectedItinerary.id}
              >
                {(() => {
                  const src = selectedItinerary.imageUrl || getDestinationImage(selectedItinerary.city, selectedItinerary.country);
                  return src ? (
                    <PeekCardImage
                      src={src}
                      alt={selectedItinerary.title}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: 160, flexShrink: 0 }}>
                      <GradientPlaceholder $gradient={STYLE_GRADIENTS[selectedItinerary.style]}>
                        <GlobeAltIcon style={{ width: 36, height: 36 }} />
                        {(selectedItinerary.city || selectedItinerary.country) && (
                          <span>{selectedItinerary.city || selectedItinerary.country}</span>
                        )}
                      </GradientPlaceholder>
                    </div>
                  );
                })()}
                <PeekCardContent>
                  <PeekCardTitle>{selectedItinerary.title}</PeekCardTitle>
                  <PeekCardMeta>
                    {selectedItinerary.country && <span>📍 {selectedItinerary.country}</span>}
                    <span>{selectedItinerary?.cost != null ? `$${selectedItinerary.cost.toLocaleString()}` : "—"}</span>
                    <span>{selectedItinerary.days} days</span>
                  </PeekCardMeta>
                  <PeekCardFooter>
                    {selectedItinerary.rating != null ? (
                      <PeekCardRating>★ {selectedItinerary.rating} ({selectedItinerary.reviewCount})</PeekCardRating>
                    ) : (
                      <NewBadge>New</NewBadge>
                    )}
                    <PeekCardLink onClick={() => navigate(`/itinerary/${selectedItinerary.slug}`)}>View Trip →</PeekCardLink>
                  </PeekCardFooter>
                </PeekCardContent>
              </MapPeekCard>
            </AnimatePresence>
          )}
        </MapPanel>
      </ExplorerLayout>

    </PageWrapper>
    </>
  );
}

// ===== Vibe Banner styled components =====
const VibeBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, rgba(66, 133, 244, 0.12), rgba(139, 92, 246, 0.08));
  border: 1px solid rgba(66, 133, 244, 0.2);
  border-radius: 14px;
  margin-bottom: 12px;
`;

const VibeBannerText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  flex: 1;
`;

const VibeBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const VibeBannerCTA = styled.button`
  padding: 6px 14px;
  border-radius: 9999px;
  border: none;
  background: #4285F4;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #1A73E8;
  }
`;

const VibeBannerDismiss = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #6b7280;
  }
`;

// ===== ITINERARY CARD SUB-COMPONENT =====
function ItineraryCardComponent({ itinerary, isSelected, isFavorited, onSelect, onToggleFavorite, onHover, onHoverEnd, registerRef }) {
  const [imgError, setImgError] = useState(false);
  const { src: wikiSrc } = useWikipediaImage(itinerary.city, itinerary.country);
  const placeholderLabel = itinerary.city || itinerary.country;

  // Image or gradient placeholder
  const renderImage = () => {
    if (imgError) {
      return (
        <GradientPlaceholder $gradient={STYLE_GRADIENTS[itinerary.style]}>
          <GlobeAltIcon />
          {placeholderLabel && <span>{placeholderLabel}</span>}
        </GradientPlaceholder>
      );
    }
    const src = itinerary.imageUrl || getDestinationImage(itinerary.city, itinerary.country) || wikiSrc;
    if (src) {
      return <img src={src} alt={itinerary.title} width={400} height={250} onError={() => setImgError(true)} />;
    }
    return (
      <GradientPlaceholder $gradient={STYLE_GRADIENTS[itinerary.style]}>
        <GlobeAltIcon />
        {placeholderLabel && <span>{placeholderLabel}</span>}
      </GradientPlaceholder>
    );
  };

  // Creator avatar or default
  const renderAvatar = (size) => {
    if (itinerary.creator.avatarUrl) {
      return <img src={itinerary.creator.avatarUrl} alt={itinerary.creator.name} width={40} height={40} />;
    }
    return (
      <DefaultAvatar $size={size}>
        <SparklesIcon />
      </DefaultAvatar>
    );
  };

  return (
    <ItinCard
      ref={registerRef}
      $selected={isSelected}
      onClick={() => onSelect(itinerary.id)}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      layout
    >
      {/* Desktop layout: compact thumbnail + text */}
      <DesktopCardInner>
        <CardThumbnail>
          {renderImage()}
        </CardThumbnail>
        <CardBody>
          <CardTitleRow>
            <CardTitle>{itinerary.title}</CardTitle>
            <FavoriteIcon
              $active={isFavorited}
              onClick={e => { e.stopPropagation(); onToggleFavorite(itinerary.id); }}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorited ? <HeartSolid /> : <HeartIcon />}
            </FavoriteIcon>
          </CardTitleRow>
          <CardMeta>
            <span className="label">Cost:</span> {itinerary.cost != null ? `$${itinerary.cost.toLocaleString()}` : "—"}
            {' '}<span style={{ margin: '0 4px' }}>&bull;</span>{' '}
            <span className="label">Days:</span> {itinerary.days}
          </CardMeta>
          <CardHighlights>
            <span className="label">Highlights:</span>{' '}
            {itinerary.highlights.join(', ')}
          </CardHighlights>
          <CardUser>
            {renderAvatar('16px')}
            <span>User: {itinerary.creator.name}</span>
          </CardUser>
        </CardBody>
      </DesktopCardInner>

      {/* Mobile layout: tall hero image */}
      <MobileCardInner>
        <MobileCardImage>
          {renderImage()}
          <MobileCardFavorite
            $active={isFavorited}
            onClick={e => { e.stopPropagation(); onToggleFavorite(itinerary.id); }}
          >
            {isFavorited ? <HeartSolid /> : <HeartIcon />}
          </MobileCardFavorite>
        </MobileCardImage>
        <MobileCardContent>
          <MobileCardTitle>{itinerary.title}</MobileCardTitle>
          <MobileCardMeta>
            <span><CurrencyDollarIcon /> {itinerary.cost != null ? `$${itinerary.cost.toLocaleString()}` : "—"}</span>
            <span><CalendarDaysIcon /> {itinerary.days} Days</span>
          </MobileCardMeta>
          {itinerary.style && <StyleBadge>{itinerary.style}</StyleBadge>}
          <MobileCardHighlights>
            {itinerary.highlights.join(', ')}
          </MobileCardHighlights>
          <MobileCardFooter>
            <ExploreLink onClick={e => { e.stopPropagation(); onSelect(itinerary.id); }}>
              Explore <ArrowRightIcon />
            </ExploreLink>
            <SaveButton
              $saved={isFavorited}
              onClick={e => { e.stopPropagation(); onToggleFavorite(itinerary.id); }}
            >
              {isFavorited ? <BookmarkSolid /> : <BookmarkIcon />}
              {isFavorited ? 'Saved' : 'Save'}
            </SaveButton>
          </MobileCardFooter>
        </MobileCardContent>
      </MobileCardInner>
    </ItinCard>
  );
}

export default ItineraryExplorerPage;
